import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import type { FC } from "react"

import { FilterChip } from "@/components/common/Filters"
import { DATE_FORMAT } from "@/constants/formats"
import type { ChangeLogCategory, ChangeLogSubCategory } from "@/graphql/codegen/graphql"
import UsersFilterChip from "@/screens/ChangeLog/components/filter/UsersFilterChip"
import { CHANGE_LOG_CATEGORY_LABELS, CHANGE_LOG_SUB_CATEGORY_LABELS } from "@/screens/ChangeLog/constants"
import type { RemoveFilterType } from "@/screens/ChangeLog/types"

const ChangeLogFilterChips: FC = () => {
  const navigate = useNavigate({ from: "/app/admin/change-log" })
  const currentSearch = useSearch({ from: "/app/admin/change-log/" })
  const { categories = [], subcategories = [], start, end, userIds = [] } = currentSearch

  const handleRemoveFilter = async (type: RemoveFilterType, value: unknown) => {
    const updatedSearch = { ...currentSearch }

    switch (type) {
      case "start":
        updatedSearch.start = undefined
        updatedSearch.end = undefined
        break
      case "end":
        updatedSearch.end = undefined
        break
      default: {
        const newVal = (currentSearch[type] ?? []).filter((filterValue) => filterValue !== value)
        updatedSearch[type] = newVal.length ? newVal : undefined
        break
      }
    }

    await navigate({
      search: updatedSearch,
      replace: true,
    })
  }

  if (!categories?.length && !subcategories?.length && !start && !end && !userIds.length) {
    return null
  }
  return (
    <Box data-testid='filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>

      {categories?.map((category: ChangeLogCategory) => (
        <FilterChip
          key={`filter-chip-${category}`}
          testId={`filter-chip-${category}`}
          label={`Category: ${CHANGE_LOG_CATEGORY_LABELS[category]}`}
          onDelete={() => handleRemoveFilter("categories", category)}
        />
      ))}

      {subcategories?.map((subCategory: ChangeLogSubCategory) => (
        <FilterChip
          key={`filter-chip-${subCategory}`}
          testId={`filter-chip-${subCategory}`}
          label={`Sub-Category: ${CHANGE_LOG_SUB_CATEGORY_LABELS[subCategory]}`}
          onDelete={() => handleRemoveFilter("subcategories", subCategory)}
        />
      ))}

      {start && (
        <FilterChip
          key='filter-chip-date-range'
          label={`Date: ${dayjs(start).format(DATE_FORMAT.US)}${end ? ` - ${dayjs(end).format(DATE_FORMAT.US)}` : ""}`}
          onDelete={() => handleRemoveFilter("start", null)}
        />
      )}

      <UsersFilterChip selectedUserIds={userIds} onRemove={handleRemoveFilter} />
    </Box>
  )
}

export default ChangeLogFilterChips
